import { useMemo, type ReactElement } from 'react';

import type { StreamState, TraceEntry } from '@harness/stream-core';

/**
 * Time to first byte, tokens per second, p95 frame time, dropped frames,
 * reconnects. Each tile pairs a sans label with a large mono tabular value
 * and its unit, in muted at a smaller size, rather than folding the unit
 * into the value's own string.
 */

type FrameTick = Extract<TraceEntry, { kind: 'frame' }>;

function percentile(sorted: readonly number[], p: number): number {
  if (sorted.length === 0) return 0;
  const index = Math.min(sorted.length - 1, Math.floor(sorted.length * p));
  return sorted[index] ?? 0;
}

function MetricTile({
  label,
  value,
  unit,
}: {
  label: string;
  value: string;
  unit: string | undefined;
}): ReactElement {
  return (
    <div className="metrics-strip__item">
      <dt>{label}</dt>
      <dd>
        {value}
        {unit !== undefined && <span className="metrics-strip__unit">{unit}</span>}
      </dd>
    </div>
  );
}

export function MetricsStrip({ state }: { state: StreamState }): ReactElement {
  const metrics = useMemo(() => {
    const frames = state.timeline.filter((e): e is FrameTick => e.kind === 'frame');
    const deltas = frames.filter((f) => f.type === 'response.output_text.delta');
    const first = state.timeline[0];
    const firstDelta = deltas[0];

    const ttfb = first !== undefined && firstDelta !== undefined ? firstDelta.at - first.at : null;

    const gaps: number[] = [];
    for (let i = 1; i < deltas.length; i += 1) {
      const prev = deltas[i - 1];
      const cur = deltas[i];
      if (prev !== undefined && cur !== undefined) gaps.push(cur.at - prev.at);
    }
    const sortedGaps = [...gaps].sort((a, b) => a - b);
    const p95FrameTime = percentile(sortedGaps, 0.95);

    const lastDeltaAt = deltas[deltas.length - 1]?.at ?? 0;
    const firstDeltaAt = deltas[0]?.at ?? 0;
    const span = deltas.length > 1 ? lastDeltaAt - firstDeltaAt : 0;
    const tokensPerSecond = span > 0 ? (deltas.length / span) * 1000 : 0;

    const dropped = state.timeline.filter(
      (e) => e.kind === 'duplicate' || e.kind === 'stale',
    ).length;

    return { ttfb, tokensPerSecond, p95FrameTime, dropped, reconnects: state.reconnects };
  }, [state.timeline, state.reconnects]);

  return (
    <dl className="metrics-strip">
      <MetricTile
        label="TTFB"
        value={metrics.ttfb === null ? 'n/a' : metrics.ttfb.toFixed(0)}
        unit={metrics.ttfb === null ? undefined : 'ms'}
      />
      <MetricTile label="throughput" value={metrics.tokensPerSecond.toFixed(1)} unit="tok/s" />
      <MetricTile label="p95 frame" value={metrics.p95FrameTime.toFixed(0)} unit="ms" />
      <MetricTile label="dropped" value={String(metrics.dropped)} unit={undefined} />
      <MetricTile label="reconnects" value={String(metrics.reconnects)} unit={undefined} />
    </dl>
  );
}
